<template>
  <div
    class="page">
    <p class="page__logo">
      <img
        src="@/assets/images/logo@2x.png">
    </p>
    <div class="page__form general-form login-form pt-4 p-md-6">
      <validation-observer v-slot="observer">
        <b-form>
          <div class="form-group">
            <label
              for="email"
              class="col-form-label">メールアドレス</label>
            <validation-provider   
              v-slot="v" 
              name="メールアドレス"
              rules="email|required">
              <b-form-input
                v-model="email"
                type="email"
                placeholder="sample@t-today.jp"
                data-cy="email-input" />
              <p class="text-primary">
                {{ v.errors[0] }}
              </p>
            </validation-provider>
          </div>
          <div class="form-group mt-3 mt-md-4">
            <label
              for="password"
              class="col-form-label">パスワード</label>
            <validation-provider
              v-slot="v" 
              name="パスワード"
              rules="password|required">
              <b-form-input
                v-model="password"
                type="password"
                placeholder="****"
                data-cy="password-input" />
              <p class="text-primary">
                {{ v.errors[0] }}
              </p>
            </validation-provider>
            <p class="mt-2 pb-3">
              <b-form-invalid-feedback
                v-for="message in nonFieldErrors"
                :key="message"
                :state="false">
                {{ message }}
              </b-form-invalid-feedback> 
              <b>パスワードをお忘れの場合は<router-link to="password_reminder">こちら</router-link></b>
            </p>
          </div>
          <div class="login-form__btn pt-3">
            <button
              type="button"
              class="btn btn-primary btn-block"
              data-cy="login-button"
              @click="observer.passes(onSubmit)">
              ログイン
            </button>
          </div>
        </b-form>
      </validation-observer>
      <div class="text-right mt-10 mb-6">
        <b-button
          variant="light"
          size="sm"
          class="col-4 offset-8"
          @click="onClickReload">
          アプリ更新
        </b-button>
      </div>

      <ios-add-to-homepopup 
        :show-popup="showPopup" 
        @clickPopup="closePopup" />
    </div>
  </div>
</template>

<script>
import { load } from 'recaptcha-v3'
import IosAddToHomepopup from '@/components/parts/IosAddToHomePopup'

export default {
  components: {
    IosAddToHomepopup,
  },
  beforeRouteLeave(to, from, next) {
    if (this.recaptcha) {
      this.recaptcha.hideBadge()
    }
    next()
  },
  data() {
    return {
      email: '',
      password: '',
      nonFieldErrors: [],
      recaptcha: null,
      showPopup: false,
      isMobile: false,
    }
  },
  async mounted() {
    if (window.innerWidth <= 992) {
      this.isMobile = true
    }
    const userAgent = window.navigator.userAgent.toLowerCase()
    const isPwa = this.$route.query.pwa

    if (isPwa) this.initPwa()
    // pwaサジェストポップアップ表示可否
    // 表示するのは、iPhoneかつsafariの場合のみ
    if (
      userAgent.indexOf('safari') !== -1 &&
      userAgent.indexOf('chrome') === -1 &&
      userAgent.indexOf('edge') === -1 &&
      /ip(hone|(o|a)d)/.test(userAgent) &&
      this.$store.state.canDisplayPwaPopup
    ) {
      this.showPopup = true
      // 一度表示したらそのタブでは表示しない
      this.$store.commit('setCanDisplayPwaPopup', false)
    }
    this.$store.commit('startLoading')
    await this.loadRecaptcha().catch(() => this.$store.commit('notify/set', {type: 'danger', message: '不明なエラーが発生しました。しばらく時間をあけてアクセスし直してください。なお、連続して発生する場合は画面下のアプリ更新を押してください。'}))
    this.$store.commit('finishLoading')
  },
  methods: {
    onClickReload() {
      location.reload(true)
    },
    async loadRecaptcha() {
      this.recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY)
      this.recaptcha.showBadge()
    },
    async onSubmit() {
      this.$store.commit('startLoading')
      
      const recaptchaToken = await this.recaptcha.execute('login')
        .catch(() => this.$store.commit('notify/set', {type: 'danger', message: '不明なエラーが発生しました。しばらく時間をあけてアクセスし直してください。なお、連続して発生する場合は画面下のアプリ更新を押してください。'}))

      if (!recaptchaToken) {
        this.$store.commit('finishLoading')
        return
      }
      const postBody = {
        email: this.email,
        password: this.password,
        recaptcha: recaptchaToken
      }
      this.$axios.post('/auth/login/', postBody)
        .then(res => {
          this.$store.commit('auth/setAccessToken', res.data.access)
          this.$store.commit('auth/setRefreshToken', res.data.refresh)
          this.$store.commit('auth/setApiDomain', res.data.user.tenant.domain)

          // 以前と別のテナントにログインしようとした場合はstoreに保存していた選択カテゴリのリストと、カテゴリをクリアする
          this.$axios.get('/tenants/').then(res => {
            const tenantInfo = res.data
            if (this.$store.state.tenant.tenantInfo?.id !== tenantInfo.id) {
              this.$store.commit('events/clearCategories')
            }
            this.$store.commit('tenant/setTenantInfo', tenantInfo)
          }).catch(err => {
            if (!err.response || !err.response.data) {
              this.$store.commit('notify/set', {type: 'danger', message: '不明なエラーが発生しました。しばらく時間をあけてアクセスし直してください。なお、連続して発生する場合は画面下のアプリ更新を押してください。'})
              return
            }
            if ('nonFieldErrors' in err.response.data) {
              this.nonFieldErrors = err.response.data.nonFieldErrors
            }
          })
          
          // 上の設定でテナント用のURLにアクセスできるようになるので, ログインユーザーの情報を取得.
          this.$axios.get('/accounts/me/').then(res=>{
            this.$store.commit('auth/setUser', res.data)
            if (res.data.homeView === 'member_list' && this.isMobile) {
              this.$router.push('/members')
            } else {
              this.$router.push(`/members/${res.data.id}`)
            }

          })
        }).catch(err => {
          if (!err.response || !err.response.data) {
            this.$store.commit('notify/set', {type: 'danger', message: '不明なエラーが発生しました。しばらく時間をあけてアクセスし直してください。なお、連続して発生する場合は画面下のアプリ更新を押してください。'})
            return
          }
          if ('nonFieldErrors' in err.response.data) {
            this.nonFieldErrors = err.response.data.nonFieldErrors
          }
        }).finally(()=> {
          this.$store.commit('finishLoading')
        })
    },
    closePopup() {
      this.showPopup = false
    },
    initPwa() {
      this.$store.commit('setCanDisplayPwaPopup', false)
    },
  }
}
</script>

<style lang="sass" scoped>
</style>